// Прогресс гостя (уроки без регистрации, lib/guest-lesson.ts, план
// .scratch/guest-mode/plan.md, шаг 2). Лежит в localStorage отдельным ключом,
// без срока: гость может вернуться через неделю, и уроки должны его дождаться
// (у очереди lib/lesson-sync.ts предел 7 дней, она только для обрывов сети).
// После входа /guest-sync переносит всё на сервер по порядку уроков
// (importGuestProgress) и стирает ключ.
//
// Ключ не входит в USER_KEY_PREFIXES (lib/user-storage.ts): вход под аккаунтом
// не должен стирать уроки, пройденные до входа.

import type { LessonPart } from '@/lib/lessons/parts'
import type { TrainerMistake } from '@/app/grammar/actions'
import { GUEST_COURSE_SLUGS, GUEST_WALL_BEFORE } from '@/lib/guest-lesson'

export const GUEST_PROGRESS_KEY = 'tk-guest-progress-v1'
// Событие для подписчиков в этой же вкладке ('storage' приходит только из других).
export const GUEST_PROGRESS_EVENT = 'tk-guest-progress'

export type GuestLesson = {
  lessonId: string
  slug: string
  sortOrder: number
  /** Закрытых частей 0..3, лучшее за все прохождения. */
  parts: number
  /** Результат части 3 (лучший). */
  score: number
  vocabIds: string[]
  /** Слова с ошибкой на проверке: в повторение через 4 часа, а не завтра. */
  missedIds: string[]
  seconds: number
  /** Слов урока для плашки "Сохраните прогресс" (у алфавита 0). */
  words: number
  timezone: string
  updatedAt: string
}

export type GuestTopic = {
  topicId: string
  correct: number
  total: number
  mistakes: TrainerMistake[]
  seconds: number
  timezone: string
  at: string
}

export type GuestProgress = { lessons: GuestLesson[]; topics: GuestTopic[] }

type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const EMPTY: GuestProgress = { lessons: [], topics: [] }

function store(): KV | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function notify() {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(GUEST_PROGRESS_EVENT))
  } catch {}
}

export function readGuestProgress(kv: KV | null = store()): GuestProgress {
  if (!kv) return EMPTY
  try {
    const raw = kv.getItem(GUEST_PROGRESS_KEY)
    if (!raw) return EMPTY
    const p = JSON.parse(raw) as Partial<GuestProgress>
    return {
      lessons: Array.isArray(p.lessons) ? p.lessons.filter((l) => l && typeof l.lessonId === 'string' && typeof l.slug === 'string') : [],
      topics: Array.isArray(p.topics) ? p.topics.filter((t) => t && typeof t.topicId === 'string') : [],
    }
  } catch {
    return EMPTY
  }
}

function writeGuestProgress(p: GuestProgress, kv: KV | null) {
  if (!kv) return
  try {
    if (p.lessons.length || p.topics.length) kv.setItem(GUEST_PROGRESS_KEY, JSON.stringify(p))
    else kv.removeItem(GUEST_PROGRESS_KEY)
  } catch {
    /* приватный режим или переполнено: прогресс не сохранится */
  }
  notify()
}

export function hasGuestProgress(kv: KV | null = store()): boolean {
  const p = readGuestProgress(kv)
  return p.lessons.length > 0 || p.topics.length > 0
}

export function clearGuestProgress(kv: KV | null = store()) {
  writeGuestProgress(EMPTY, kv)
}

export type GuestPartInput = {
  lessonId: string
  slug: string
  sortOrder: number
  part: LessonPart
  score: number | null
  vocabIds: string[]
  missedIds: string[]
  seconds: number
  words: number
  timezone: string
}

/** Часть урока закрыта гостем. Повтор уже закрытой части прогресс не уменьшает. */
export function recordGuestPart(input: GuestPartInput, kv: KV | null = store(), now = new Date()): GuestProgress {
  const p = readGuestProgress(kv)
  const prev = p.lessons.find((l) => l.lessonId === input.lessonId)
  const next: GuestLesson = {
    lessonId: input.lessonId,
    slug: input.slug,
    sortOrder: input.sortOrder,
    parts: Math.max(prev?.parts ?? 0, input.part),
    score: Math.max(prev?.score ?? 0, input.part === 3 ? (input.score ?? 0) : 0),
    vocabIds: input.vocabIds,
    missedIds: input.part === 1 ? [...new Set([...(prev?.missedIds ?? []), ...input.missedIds])] : (prev?.missedIds ?? input.missedIds),
    seconds: (prev?.seconds ?? 0) + Math.max(0, Math.round(input.seconds)),
    words: input.words,
    timezone: input.timezone,
    updatedAt: now.toISOString(),
  }
  const result = { ...p, lessons: [...p.lessons.filter((l) => l.lessonId !== input.lessonId), next] }
  writeGuestProgress(result, kv)
  return result
}

/** Результат тренажёра правила в уроке: на сервер после входа, без опыта и серии. */
export function recordGuestTopics(topics: Omit<GuestTopic, 'at'>[], kv: KV | null = store(), now = new Date()) {
  const p = readGuestProgress(kv)
  writeGuestProgress({ ...p, topics: [...p.topics, ...topics.map((t) => ({ ...t, at: now.toISOString() }))].slice(-60) }, kv)
}

export function guestPartsDone(p: GuestProgress, lessonId: string): number {
  return p.lessons.find((l) => l.lessonId === lessonId)?.parts ?? 0
}

const doneSlugs = (p: GuestProgress) => new Set(p.lessons.filter((l) => l.parts >= 3).map((l) => l.slug))

export type GuestSummary = {
  /** Пройдено уроков курса (из GUEST_COURSE_SLUGS.length гостевых, с 25.10.2026 — четырёх). */
  lessons: number
  /** Слов в уроках, где закрыта хотя бы часть "Слова". */
  words: number
  /** Следующий урок курса для гостя; GUEST_WALL_BEFORE, если пройдены все гостевые. */
  next: string
  /** Номер следующего урока (1..GUEST_COURSE_SLUGS.length+1).*/
  nextNumber: number
  /** Есть ли что сохранять. */
  any: boolean
}

export function guestSummary(p: GuestProgress): GuestSummary {
  const done = doneSlugs(p)
  const course = GUEST_COURSE_SLUGS as readonly string[]
  const idx = course.findIndex((s) => !done.has(s))
  return {
    lessons: course.filter((s) => done.has(s)).length,
    words: p.lessons.filter((l) => l.parts >= 1).reduce((sum, l) => sum + (l.words || 0), 0),
    next: idx === -1 ? GUEST_WALL_BEFORE : course[idx],
    nextNumber: idx === -1 ? course.length + 1 : idx + 1,
    any: p.lessons.length > 0,
  }
}

/**
 * Цепочка для гостя (сервер её не проверяет): урок курса открыт, если пройден
 * предыдущий урок курса в этом браузере. Алфавит и первый урок открыты всегда.
 * Возвращает null, если можно, иначе урок, куда вести.
 */
export function guestChainRedirect(slug: string, p: GuestProgress): string | null {
  const course = GUEST_COURSE_SLUGS as readonly string[]
  const i = course.indexOf(slug)
  if (i <= 0) return null
  const done = doneSlugs(p)
  if (done.has(slug) || done.has(course[i - 1])) return null
  return guestSummary(p).next
}

export type ImportStep =
  | { kind: 'part'; lesson: GuestLesson; part: LessonPart }
  | { kind: 'topic'; topic: GuestTopic }

/**
 * План переноса: уроки по порядку курса; пройденный урок одной частью 3 (сервер
 * сам кладёт слова в повторение и начисляет 15 опыта), начатый частями по
 * порядку; результаты тренажёра после уроков.
 */
export function importPlan(p: GuestProgress): ImportStep[] {
  const steps: ImportStep[] = []
  for (const lesson of [...p.lessons].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (lesson.parts >= 3) steps.push({ kind: 'part', lesson, part: 3 })
    else for (let n = 1; n <= lesson.parts; n++) steps.push({ kind: 'part', lesson, part: n as LessonPart })
  }
  for (const topic of [...p.topics].sort((a, b) => a.at.localeCompare(b.at))) steps.push({ kind: 'topic', topic })
  return steps
}

export type ImportSenders = {
  part: (lesson: GuestLesson, part: LessonPart) => Promise<unknown>
  topic: (topic: GuestTopic) => Promise<unknown>
}

/**
 * Переносит прогресс по плану. Урок снимается из браузера, когда ушли все его
 * части, результат тренажёра - сразу; на первой ошибке перенос стоит, остаток
 * ждёт следующей попытки (повтор частей 1-2 сервер отсекает сам, повтор части 3
 * в гостевом режиме опыта не даёт). Возвращает число отправленных шагов.
 */
export async function importGuestProgress(send: ImportSenders, kv: KV | null = store()): Promise<{ sent: number; failed: boolean }> {
  let sent = 0
  for (const step of importPlan(readGuestProgress(kv))) {
    try {
      if (step.kind === 'part') {
        await send.part(step.lesson, step.part)
        const last = step.lesson.parts >= 3 ? 3 : step.lesson.parts
        if (step.part === last) {
          const p = readGuestProgress(kv)
          writeGuestProgress({ ...p, lessons: p.lessons.filter((l) => l.lessonId !== step.lesson.lessonId) }, kv)
        }
      } else {
        await send.topic(step.topic)
        const p = readGuestProgress(kv)
        writeGuestProgress({ ...p, topics: p.topics.filter((t) => !(t.at === step.topic.at && t.topicId === step.topic.topicId)) }, kv)
      }
      sent++
    } catch {
      return { sent, failed: true }
    }
  }
  return { sent, failed: false }
}
